/**
 * Shared date utilities used across components.
 * Handles null, undefined, and invalid dates safely.
 */

/** Parse date-only (YYYY-MM-DD) as local time to avoid timezone shift */
function parseDateSafe(dateStr: string | undefined | null): Date | null {
  if (!dateStr) return null;
  try {
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return new Date(s + 'T00:00:00');
    }
    return new Date(s);
  } catch {
    return null;
  }
}

/** Ensure UTC timestamps from SQLite (no Z suffix) are parsed as UTC */
function toLocalDate(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  try {
    if (/[Zz+-]\d{2}:?\d{2}$/.test(iso) || iso.endsWith('Z')) {
      return new Date(iso);
    }
    return new Date(iso + 'Z');
  } catch {
    return null;
  }
}

/**
 * Format a date string to short readable form: "Mon DD, YYYY" style (e.g. "Feb 12, 2025").
 * Returns "—" for null, undefined, or invalid dates.
 */
export function formatShortDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  try {
    const d = parseDateSafe(dateStr);
    if (!d || isNaN(d.getTime())) return dateStr.split('T')[0] || '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * Relative time string: "2 hours ago", "yesterday", "just now", etc.
 * Handles future dates: "in 2h", "tomorrow".
 * Returns empty string for null, undefined, or invalid dates.
 */
export function timeAgo(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  const d = toLocalDate(dateStr) ?? new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 0) {
    const absS = Math.abs(seconds);
    if (absS < 3600) return `in ${Math.ceil(absS / 60)}m`;
    if (absS < 86400) return `in ${Math.ceil(absS / 3600)}h`;
    const days = Math.ceil(absS / 86400);
    if (days === 1) return 'tomorrow';
    return `in ${days}d`;
  }
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Calculate day variance between due and resolved dates.
 * Positive days = completed before due (early), Negative = after due (late).
 * Returns null when either date is missing or invalid.
 */
export function calcVariance(
  dueRaw: string | undefined | null,
  resolvedRaw: string | undefined | null
): { days: number; label: string; color: string } | null {
  if (!dueRaw || !resolvedRaw) return null;
  try {
    const due = new Date(dueRaw);
    const resolved = new Date(resolvedRaw);
    if (isNaN(due.getTime()) || isNaN(resolved.getTime())) return null;
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const resDay = new Date(resolved.getFullYear(), resolved.getMonth(), resolved.getDate());
    const diffMs = dueDay.getTime() - resDay.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) return { days: diffDays, label: `${diffDays}d early`, color: '#4caf50' };
    if (diffDays < 0) return { days: diffDays, label: `${Math.abs(diffDays)}d late`, color: '#CF6679' };
    return { days: 0, label: 'Same day', color: '#4caf50' };
  } catch {
    return null;
  }
}

/**
 * Format date as compact "M/D" (e.g. "3/4").
 */
export function formatCompactDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  try {
    const d = parseDateSafe(dateStr);
    if (!d || isNaN(d.getTime())) return '—';
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return '—';
  }
}

/**
 * Format date as "Month DD, YYYY" (e.g. "March 4, 2025").
 */
export function formatDateLong(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  try {
    const d = parseDateSafe(dateStr);
    if (!d || isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * Format date and time as "Mon DD, YYYY HH:MM" (e.g. "Mar 4, 2025 2:30 PM").
 */
export function formatDateTimeShort(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '—';
  }
}

/**
 * Get YYYY-MM-DD from a Date.
 */
export function formatIsoDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Format week label: "Wk M/D-M/D" for Mon-Sun week.
 */
export function formatWeekLabel(mondayStr: string | undefined | null): string {
  if (!mondayStr) return '—';
  try {
    const monday = new Date(mondayStr);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const endStr = sunday.toISOString().split('T')[0];
    return `Wk ${formatCompactDate(mondayStr)}-${formatCompactDate(endStr)}`;
  } catch {
    return '—';
  }
}

/**
 * Format week label long: "Week of Mon DD - Sun DD".
 */
export function formatWeekLabelLong(mondayStr: string | undefined | null): string {
  if (!mondayStr) return '—';
  try {
    const monday = new Date(mondayStr);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const endStr = sunday.toISOString().split('T')[0];
    return `Week of ${formatShortDate(mondayStr)} - ${formatShortDate(endStr)}`;
  } catch {
    return '—';
  }
}
